import React from 'react'
import { formatter } from '../Helper/Money'
import { CardTransactionSummary } from '../Type/ViewTransaction'

interface CardSummaryTableProps {
  cardSummaries: CardTransactionSummary[]
}

interface AggregatedTotals {
  purchases: {
    pending: { count: number; amount: number }
    settled: { count: number; amount: number }
  }
  refunds: {
    pending: { count: number; amount: number }
    settled: { count: number; amount: number }
  }
}

function formatAmount (amount: number, negate = false): string {
  // optionally negate values for money formatter to add parenthesis. Ex. like refund money values
  // negate only when value greater than 0 to avoid money formatter adding parenthesis
  return formatter.format(negate && amount > 0 ? -amount : amount)
}

const initialTotals = (): AggregatedTotals => ({
  purchases: { pending: { count: 0, amount: 0 }, settled: { count: 0, amount: 0 } },
  refunds: { pending: { count: 0, amount: 0 }, settled: { count: 0, amount: 0 } },
})

// Accumulator function to calculate total count, amount and net of card transactions.
// Instead of looping and calculating and mutating the data in place, using a reducer
// by taking list of transactions and accumulating the values and returning it as a new
// object of results.
function calculateTotals (
  accumulator: AggregatedTotals,
  current: CardTransactionSummary
): AggregatedTotals {
  return {
    purchases: {
      pending: {
        count: accumulator.purchases.pending.count + current.metrics.purchases.pending.count,
        amount: accumulator.purchases.pending.amount + current.metrics.purchases.pending.amount,
      },
      settled: {
        count: accumulator.purchases.settled.count + current.metrics.purchases.settled.count,
        amount: accumulator.purchases.settled.amount + current.metrics.purchases.settled.amount,
      },
    },
    refunds: {
      pending: {
        count: accumulator.refunds.pending.count + current.metrics.refunds.pending.count,
        amount: accumulator.refunds.pending.amount + current.metrics.refunds.pending.amount,
      },
      settled: {
        count: accumulator.refunds.settled.count + current.metrics.refunds.settled.count,
        amount: accumulator.refunds.settled.amount + current.metrics.refunds.settled.amount,
      },
    },
  }
}

export function CardSummaryTable (props: CardSummaryTableProps) {
  const totals = props.cardSummaries?.reduce(calculateTotals, initialTotals()) || initialTotals()

  return (
    <table className="data-table card-summary">
      <thead>
        <tr>
          <th colSpan={2}></th>
          <th colSpan={2}>Pending Settlement Summary</th>
          <th colSpan={2}>Settlement Summary</th>
        </tr>

        <tr>
          <th>Credit Card Type</th>
          <th>&nbsp;</th>
          <th>Count</th>
          <th>Amount</th>
          <th>Count</th>
          <th>Amount</th>
        </tr>
      </thead>

      <tbody>
        {props.cardSummaries?.map((value: CardTransactionSummary, index: number) => {
          const { purchases, refunds } = value.metrics

          return (
            <React.Fragment key={`card-summary-${value.cardBrand}-${index}`}>
              <tr>
                <td className="credit-card-type" rowSpan={3}>
                  {value.cardBrand?.toUpperCase()}
                </td>
                <td>Purchases</td>
                <td>{purchases.pending.count}</td>
                <td>{formatAmount(purchases.pending.amount)}</td>
                <td>{purchases.settled.count}</td>
                <td>{formatAmount(purchases.settled.amount)}</td>
              </tr>
              <tr>
                <td>Refunds</td>
                <td>{refunds.pending.count}</td>
                <td>{formatAmount(refunds.pending.amount, true)}</td>
                <td>{refunds.settled.count}</td>
                <td>{formatAmount(refunds.settled.amount, true)}</td>
              </tr>
              <tr>
                <td>Net</td>
                <td>
                  {value.metrics.purchases.pending.count + value.metrics.refunds.pending.count}
                </td>
                <td>{formatAmount(purchases.pending.amount - refunds.pending.amount)}</td>
                <td>
                  {value.metrics.purchases.settled.count + value.metrics.refunds.settled.count}
                </td>
                <td>
                  {formatAmount(purchases.settled.amount - refunds.settled.amount)}
                </td>
              </tr>
            </React.Fragment>
          )
        },
        )}

        { /* aggregation of summary totals */ }
        <tr>
          <td className="credit-card-type" rowSpan={3}>totals</td>
          <td>Purchases</td>
          <td>{totals.purchases.pending.count}</td>
          <td>{formatAmount(totals.purchases.pending.amount)}</td>
          <td>{totals.purchases.settled.count}</td>
          <td>{formatAmount(totals.purchases.settled.amount)}</td>
        </tr>
        <tr>
          <td>Refunds</td>
          <td>{totals.refunds.pending.count}</td>
          <td>{formatAmount(totals.refunds.pending.amount, true)}</td>
          <td>{totals.refunds.settled.count}</td>
          <td>{formatAmount(totals.refunds.settled.amount, true)}</td>
        </tr>
        <tr>
          <td>Net</td>
          <td>{totals.purchases.pending.count + totals.refunds.pending.count}</td>
          <td>
            {formatAmount(totals.purchases.pending.amount - totals.refunds.pending.amount)}
          </td>
          <td>{totals.purchases.settled.count + totals.refunds.settled.count}</td>
          <td>
            {formatAmount(totals.purchases.settled.amount - totals.refunds.settled.amount)}
          </td>
        </tr>

      </tbody>
    </table>
  )
}
